"use client";

import React, { useState, useEffect } from "react";

type Props = {
    refiLoanAmount: number;
    setRefiLoanAmount: (n: number) => void;
    refiTerm: number;
    setRefiTerm: (n: number) => void;
    refiRate: number;
    setRefiRate: (n: number) => void;
};

const formatWithCommas = (value: number | string | undefined): string =>
    typeof value === "number" || typeof value === "string"
        ? value.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ",")
        : "";

const RefiLoan = ({ refiLoanAmount, setRefiLoanAmount, refiTerm, setRefiTerm, refiRate, setRefiRate }: Props) => {
    const [loanInput, setLoanInput] = useState("0");
    const [isOpen, setIsOpen] = useState(false);

    useEffect(() => {
        setLoanInput(formatWithCommas(refiLoanAmount));
    }, [refiLoanAmount]);

    return (
        <div className="border border-gray-300 overflow-hidden shadow 2xl:w-[900px]">
            <button
                type="button"
                className="w-full text-left bg-[#202358] text-white px-4 py-3 cursor-pointer select-none flex items-center justify-between hover:bg-blue active:bg-blue focus:bg-blue 2xl:h-[42px]"
                onClick={() => setIsOpen(!isOpen)}
            >
                <span className="text-[18px] font-bold uppercase">Refi LOAN</span>
                <span className="text-xl">{isOpen ? "−" : "+"}</span>
            </button>

            <div
                className={`transition-all duration-300 ease-in-out overflow-hidden ${
                    isOpen ? "max-h-[1000px] opacity-100" : "max-h-0 opacity-0"
                }`}
            >
                <div className="bg-white p-2 2xl:w-[898px]">
                    {/* New Loan Amount */}
                    <div className="flex flex-col gap-3 2xl:flex 2xl:flex-row 2xl:items-start 2xl:gap-2">
                        <div className="2xl:w-[367px] 2xl:flex 2xl:justify-end">
                            <label
                                htmlFor="refiLoanAmount"
                                className="w-32 font-medium text-black content-center text-[15px] 2xl:w-[140px]"
                            >
                                New Loan Amount: $
                            </label>
                            <input
                                id="refiLoanAmount"
                                type="text"
                                inputMode="numeric"
                                className="border px-3 py-1 rounded w-36 text-left 2xl:w-[122px] 2xl:h-[42px] bg-[#f6f9ff] text-[14px]"
                                value={loanInput}
                                onChange={(e) => {
                                    const raw = e.target.value.replace(/,/g, "");
                                    if (/^\d*$/.test(raw)) {
                                        const numeric = Number(raw);
                                        setRefiLoanAmount(numeric);
                                        setLoanInput(formatWithCommas(raw));
                                    }
                                }}
                                onBlur={() => setLoanInput(formatWithCommas(refiLoanAmount))}
                            />
                        </div>
                        <input
                            aria-label="New loan amount"
                            type="range"
                            min={0}
                            max={10000000}
                            step={1000}
                            value={refiLoanAmount}
                            onChange={(e) => {
                                const val = Number(e.target.value);
                                setRefiLoanAmount(val);
                                setLoanInput(formatWithCommas(val));
                            }}
                            className="w-full 2xl:w-[506px] accent-blue"
                        />
                    </div>

                    <div className="flex justify-end text-xs text-black 2xl:-mt-1">
                        <div className="pb-10 justify-between w-full max-w-md 2xl:pb-0 flex 2xl:justify-between 2xl:w-full 2xl:max-w-md 2xl:text-[10px] 2xl:-mt-3.5 2xl:mb-2.5">
                            <span>$0</span>
                            <span>$100K</span>
                            <span>$1M</span>
                            <span>$10M</span>
                        </div>
                    </div>

                    {/* Term (Years) */}
                    <div className="flex flex-col gap-3 2xl:flex 2xl:flex-row 2xl:items-start 2xl:gap-2">
                        <div className="2xl:w-[367px] 2xl:flex 2xl:justify-end">
                            <label
                                htmlFor="refiTerm"
                                className="w-32 font-medium text-black content-center text-[15px] 2xl:w-[88px]"
                            >
                                Term (Years):
                            </label>
                            <input
                                id="refiTerm"
                                type="number"
                                className="border px-3 py-1 rounded w-36 text-left 2xl:w-[122px] 2xl:h-[42px] bg-[#f6f9ff] text-[14px]"
                                value={refiTerm}
                                onChange={(e) => setRefiTerm(Number(e.target.value))}
                            />
                        </div>
                        <input
                            aria-label="Term in years"
                            type="range"
                            min={0}
                            max={40}
                            step={1}
                            value={refiTerm}
                            onChange={(e) => setRefiTerm(Number(e.target.value))}
                            className="w-full 2xl:w-[506px] accent-blue"
                        />
                    </div>

                    <div className="flex justify-end text-xs text-black 2xl:-mt-1">
                        <div className="pb-10 justify-between w-full max-w-md 2xl:pb-0 flex 2xl:justify-between 2xl:w-full 2xl:max-w-md 2xl:text-[10px] 2xl:-mt-3.5 2xl:mb-2.5">
                            <span>0</span>
                            <span>13</span>
                            <span>26</span>
                            <span>40</span>
                        </div>
                    </div>

                    {/* Interest Rate */}
                    <div className="flex flex-col gap-3 2xl:flex 2xl:flex-row 2xl:items-start 2xl:gap-2">
                        <div className="2xl:w-[367px] 2xl:flex 2xl:justify-end">
                            <label
                                htmlFor="refiRate"
                                className="w-32 font-medium text-black content-center text-[15px] 2xl:w-[106px]"
                            >
                                Interest Rate: %
                            </label>
                            <input
                                id="refiRate"
                                type="number"
                                className="border px-3 py-1 rounded w-36 text-left 2xl:w-[122px] 2xl:h-[42px] bg-[#f6f9ff] text-[14px]"
                                value={refiRate}
                                onChange={(e) => setRefiRate(Number(e.target.value))}
                            />
                        </div>
                        <input
                            aria-label="Interest rate"
                            type="range"
                            min={0}
                            max={10}
                            step={0.1}
                            value={refiRate}
                            onChange={(e) => setRefiRate(Number(e.target.value))}
                            className="w-full 2xl:w-[506px] accent-blue"
                        />
                    </div>

                    <div className="flex justify-end text-xs text-black 2xl:-mt-1">
                        <div className="pb-10 justify-between w-full max-w-md 2xl:pb-0 flex 2xl:justify-between 2xl:w-full 2xl:max-w-md 2xl:text-[10px] 2xl:-mt-3.5 2xl:mb-2.5">
                            <span>0%</span>
                            <span>3.5%</span>
                            <span>6.5%</span>
                            <span>10%</span>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default RefiLoan;
